import { useDebouncedValue } from '@/hooks'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import {
    centroCusto,
    centroCusto_v2,
    empresas,
    funcionario,
    local,
} from 'service/api'
import type { PaginatedResponse } from 'service/api/types'
import { useStore } from 'store'
import { mapRenderCentros } from 'templates/Cadastro/CentroCusto/mapArvore'
import {
    type Escopo,
    type FacetaId,
    queryParamsDoEscopo,
    type Registro,
    um,
    varios,
} from './escopo'

/** situação que o servidor calcula para cada funcionário (#1870) */
export type SituacaoFuncionario = 'ativo' | 'emDesligamento' | 'demitido'

export type Opcao = {
    id: number
    label: string
    descricao?: string
    titleDescricao?: string
    indent?: number
    situacao?: SituacaoFuncionario
}

export type FacetaDados = {
    opcoes: Opcao[]
    carregando: boolean
    vazioTexto: string
    /** só a faceta de funcionário pagina: há mais páginas no servidor */
    temMais?: boolean
    /** carrega a próxima página e devolve o que veio nela */
    carregarMais?: () => Promise<Opcao[]>
    carregandoMais?: boolean
}

export type FacetasDados = {
    empresas: FacetaDados
    centrosCusto: FacetaDados
    locais: FacetaDados
    funcionarios: FacetaDados
    /** funcionários que sobram depois do funil (empresa ∩ CC ∩ locais) */
    totalNoEscopo: number
    /** funcionários ativos da empresa, antes do funil */
    totalDaEmpresa: number
    /** funcionário escolhido; buscado por id quando não está nas páginas carregadas */
    funcionarioSelecionado?: Opcao
}

type FuncionarioDaFaceta = {
    id: number
    nome: string
    dtRescisao?: string | Date | null
    situacao?: SituacaoFuncionario
}

/** tamanho de página da lista de funcionário, igual ao do SelectFuncionario */
const FUNCIONARIOS_POR_PAGINA = 25

/**
 * Define a descrição e title da opção baseado na situação do funcionário,
 * considerando data de rescisão, se já aconteceu ou se vai acontecer.
 */
function descricaoFuncionario(
    funcionario: FuncionarioDaFaceta,
): Pick<Opcao, 'descricao' | 'titleDescricao'> {
    if (!funcionario.dtRescisao) return {}

    const data = dayjs(funcionario.dtRescisao).format('DD/MM/YYYY')

    if (funcionario.situacao === 'emDesligamento')
        return {
            descricao: data,
            titleDescricao: 'A rescisão vai acontecer nesta data',
        }
    else if (funcionario.situacao === 'demitido')
        return {
            descricao: data,
            titleDescricao: 'A rescisão aconteceu nesta data',
        }
    else return {}
}

function opcaoDeFuncionario(funcionario: FuncionarioDaFaceta): Opcao {
    return {
        id: funcionario.id,
        label: funcionario.nome,
        situacao: funcionario.situacao,
        ...descricaoFuncionario(funcionario),
    }
}

export function useFacetaOptions(
    escopo: Escopo,
    aberto: boolean,
    registro: Registro[] = [],
    busca = '',
): FacetasDados {
    const competencia = useStore(state => state.competencia)
    const funcionarioId = useStore(state => state.employeeId)
    const buscaDebounced = useDebouncedValue(busca)

    const empresaIds = varios(escopo.empresas)
    const dependeDeEmpresa = aberto && empresaIds.length > 0
    const isListaTudo = (id: FacetaId) =>
        registro.some(parte => parte.id === id && parte.listAll)

    const { data: empresasData = [], isFetching: carregandoEmpresas } =
        empresas.useFetch()

    const {
        data: centrosDaEmpresa = [],
        isFetching: carregandoCentrosEmpresa,
    } = centroCusto.useFetch({
        queryParams: { empresaIds },
        enabled: dependeDeEmpresa && !isListaTudo('centrosCusto'),
    })

    const {
        data: centrosDoCliente = [],
        isFetching: carregandoCentrosCliente,
    } = centroCusto_v2.useFetch({
        enabled: aberto && isListaTudo('centrosCusto'),
    })

    const { data: locaisDaEmpresa = [], isFetching: carregandoLocaisEmpresa } =
        local.useFetch({
            queryParams: { empresaIds },
            enabled: dependeDeEmpresa && !isListaTudo('locais'),
        })

    const { data: locaisDoCliente = [], isFetching: carregandoLocaisCliente } =
        local.all.useFetch({
            enabled: aberto && isListaTudo('locais'),
        })

    const incluirDemitidos = !!escopo.incluirDemitidos
    const funcionarioEscolhidoId =
        funcionarioId > 0 ? funcionarioId : (um(escopo.funcionarios) ?? 0)
    // a lista alimenta as setas de navegar funcionário, que ficam fora do
    // painel: com um escolhido, ela carrega mesmo com o painel fechado
    const shouldCarregarFuncionarios =
        empresaIds.length > 0 && (aberto || funcionarioEscolhidoId > 0)

    const {
        data: paginas,
        isFetching: carregandoFuncionarios,
        isFetchingNextPage: carregandoMaisFuncionarios,
        hasNextPage,
        fetchNextPage,
    } = funcionario.useInfiniteScroll<FuncionarioDaFaceta>({
        queryParams: {
            ...queryParamsDoEscopo(escopo),
            incluirDemitidos,
            competencia,
            ...(buscaDebounced && { search: buscaDebounced }),
        } as any,
        perPage: FUNCIONARIOS_POR_PAGINA,
        enabled: shouldCarregarFuncionarios,
    })

    // total da empresa = antes do funil de centro de custo e local; uma página
    // de um item basta, o que interessa é o `meta.total`
    const { data: totalizador } = funcionario.useFetch<
        PaginatedResponse<FuncionarioDaFaceta>
    >({
        queryParams: {
            empresaIds,
            incluirDemitidos,
            competencia,
            page: 1,
            perPage: 1,
        } as any,
        enabled: dependeDeEmpresa,
    })

    const centrosData = isListaTudo('centrosCusto')
        ? centrosDoCliente
        : centrosDaEmpresa
    const carregandoCentros = isListaTudo('centrosCusto')
        ? carregandoCentrosCliente
        : carregandoCentrosEmpresa

    const locaisData = isListaTudo('locais') ? locaisDoCliente : locaisDaEmpresa
    const carregandoLocais = isListaTudo('locais')
        ? carregandoLocaisCliente
        : carregandoLocaisEmpresa

    const funcionariosCarregados = useMemo(
        () => paginas?.pages.flatMap(pagina => pagina.data) ?? [],
        [paginas],
    )

    const { data: funcionarioForaDaLista } =
        funcionario.useFetchById<FuncionarioDaFaceta>(funcionarioEscolhidoId, {
            enabled:
                !!funcionarioEscolhidoId &&
                !funcionariosCarregados.some(
                    item => item.id === funcionarioEscolhidoId,
                ),
        })

    return {
        empresas: {
            opcoes: empresasData.map(empresa => ({
                id: empresa.id,
                label: empresa.nomeFantasia,
            })),
            carregando: carregandoEmpresas,
            vazioTexto: 'Nenhuma empresa cadastrada',
        },
        centrosCusto: {
            opcoes: mapRenderCentros(centrosData).map(node => ({
                id: node.id,
                label: node.nomeCentroCusto,
                descricao: node.codigo,
                titleDescricao: 'Código do centro de custo',
                indent: node.space,
            })),
            carregando: carregandoCentros,
            vazioTexto: empresaIds.length
                ? 'Nenhum centro de custo cadastrado'
                : 'Escolha uma empresa primeiro',
        },
        locais: {
            opcoes: locaisData.map(item => ({
                id: item.id,
                label: item.nomeLocal,
                descricao: `${Number(item.latitude).toFixed(4)} ${Number(item.longitude).toFixed(4)}`,
                titleDescricao: 'Coordenadas do local',
            })),
            carregando: carregandoLocais,
            vazioTexto: empresaIds.length
                ? 'Nenhum local cadastrado'
                : 'Escolha uma empresa primeiro',
        },
        funcionarios: {
            opcoes: funcionariosCarregados.map(opcaoDeFuncionario),
            carregando: carregandoFuncionarios && !carregandoMaisFuncionarios,
            vazioTexto: empresaIds.length
                ? 'Nenhum funcionário encontrado a partir dos filtros'
                : 'Escolha uma empresa primeiro',
            temMais: !!hasNextPage,
            carregarMais: async () => {
                const resultado = await fetchNextPage()
                const novos = resultado.data?.pages.at(-1)?.data ?? []
                return novos.map(opcaoDeFuncionario)
            },
            carregandoMais: carregandoMaisFuncionarios,
        },
        totalNoEscopo: paginas?.pages[0]?.meta?.total ?? 0,
        totalDaEmpresa: totalizador?.meta?.total ?? 0,
        funcionarioSelecionado: [
            ...funcionariosCarregados,
            ...(funcionarioForaDaLista ? [funcionarioForaDaLista] : []),
        ]
            .filter(item => item.id === funcionarioEscolhidoId)
            .map(opcaoDeFuncionario)[0],
    }
}
